import { Avatar, AvatarGroup } from "@heroui/avatar";
import { ArrowRight01Icon } from "@icons";
import Image from "next/image";
import Link from "next/link";
import { lazy, Suspense, useEffect, useState } from "react";
import { StarFilledIcon } from "@/components/shared/icons";
import { RaisedButton } from "@/components/ui/raised-button";
import Spinner from "@/components/ui/spinner";
import { useGitHubContributors } from "@/hooks/useGitHubContributors";

import LargeHeader from "../shared/LargeHeader";

// Import the GitHubContributor type
interface GitHubContributor {
  login: string;
  id: number;
  avatar_url: string;
  html_url: string;
  contributions: number;
  type?: string;
}

// Lazy load the contributors section when it comes into view
const LazyContributors = lazy(() =>
  Promise.resolve({
    default: ({
      contributors,
      totalCount,
    }: {
      contributors: GitHubContributor[];
      totalCount: number;
    }) => (
      <AvatarGroup
        isBordered
        max={8}
        renderCount={(count) => (
          <p className="ms-2 text-small font-medium text-foreground text-nowrap">
            +{count} others
          </p>
        )}
        total={totalCount}
        classNames={{ base: "flex-wrap justify-center max-w-xs sm:max-w-none" }}
      >
        {contributors.map((contributor) => (
          <Avatar
            key={contributor.login}
            src={contributor.avatar_url}
            name={contributor.login}
            as={Link}
            href={contributor.html_url}
            target="_blank"
            className="cursor-pointer transition-transform hover:scale-110"
          />
        ))}
      </AvatarGroup>
    ),
  }),
);

export default function OpenSource() {
  const [isVisible, setIsVisible] = useState(false);
  const {
    data: contributorsData,
    isLoading,
    isError,
  } = useGitHubContributors("theexperiencecompany/gaia");

  const contributors = contributorsData?.contributors || [];
  const totalCount = contributorsData?.totalCount || 0;

  // Use Intersection Observer to load contributors only when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    const section = document.getElementById("opensource-section");
    if (section) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);
  return (
    <div
      id="opensource-section"
      className="flex flex-col items-center justify-center gap-10 px-4 sm:px-6 lg:px-8"
    >
      <div className="flex w-full max-w-7xl flex-col items-center justify-center rounded-2xl bg-gradient-to-b from-zinc-900 to-zinc-950 px-4 py-6 outline-1 outline-zinc-900 sm:rounded-3xl sm:p-8 lg:rounded-4xl lg:p-10">
        <LargeHeader
          headingText="Your data stays yours"
          subHeadingText="GAIA is fully open source. Run it on your own server, audit every line of code, and never worry about your data being sold or misused."
          centered
        />
        <div className="flex -space-x-8 sm:-space-x-12 lg:-space-x-16">
          <Image
            src={"/images/icons/docker3d.webp"}
            alt="Docker Logo"
            width={200}
            height={200}
            loading="lazy"
            className="relative z-[1] h-24 w-24 -rotate-10 sm:h-32 sm:w-32 lg:h-48 lg:w-48 xl:h-[200px] xl:w-[200px]"
          />
          <Image
            src={"/images/icons/github3d.webp"}
            alt="GitHub Logo"
            width={200}
            height={200}
            loading="lazy"
            className="relative z-[2] h-24 w-24 rotate-3 sm:h-32 sm:w-32 lg:h-48 lg:w-48 xl:h-[200px] xl:w-[200px]"
          />
        </div>

        {/* Contributors Section */}
        <div className="flex w-full flex-col items-center gap-3 pt-4 sm:gap-4 sm:pt-6">
          <h3 className="w-full text-center text-base font-medium text-zinc-300 sm:text-lg">
            Built by engineers who use it every day
          </h3>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-white sm:h-8 sm:w-8"></div>
              <span className="text-sm text-zinc-400 sm:text-base">
                Loading contributors...
              </span>
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400 sm:text-base">
                Failed to load contributors
              </span>
            </div>
          ) : isVisible ? (
            <Suspense
              fallback={
                <div className="flex items-center gap-2">
                  <Spinner />
                  <span className="text-sm text-zinc-400 sm:text-base">
                    Loading contributors...
                  </span>
                </div>
              }
            >
              <LazyContributors
                contributors={contributors}
                totalCount={totalCount}
              />
            </Suspense>
          ) : (
            <div className="h-8 w-full sm:h-12" />
          )}
        </div>
        <div className="flex w-full flex-col gap-3 pt-6 sm:w-auto sm:flex-row sm:pt-8 lg:pt-10">
          <Link href={"https://docs.heygaia.io"} className="w-full sm:w-auto">
            <RaisedButton
              className="w-full justify-center rounded-xl text-white! before:rounded-xl hover:scale-110 sm:w-auto"
              color="#292929"
            >
              Read Docs
              <ArrowRight01Icon width={15} />
            </RaisedButton>
          </Link>
          <Link
            href={"https://github.com/theexperiencecompany/gaia"}
            className="w-full sm:w-auto"
          >
            <RaisedButton
              className="w-full justify-center rounded-xl text-black! before:rounded-xl hover:scale-110 sm:w-auto"
              color="#00bbff"
            >
              Star on GitHub <StarFilledIcon width={17} height={17} />
            </RaisedButton>
          </Link>
        </div>
        <p className="text-sm text-zinc-500 text-center pt-4">
          Considering self-hosted alternatives?{" "}
          <Link
            href="/compare/openclaw"
            className="text-zinc-400 underline underline-offset-2 hover:text-zinc-200 transition-colors"
          >
            GAIA vs OpenClaw.
          </Link>
        </p>
      </div>
    </div>
  );
}
